/**
 * Nutrition for a day, section 5.
 *
 * "The day's calorie figure shown to the user is dayPlan(...).k. There is no separate
 * target constant anywhere in the app." Nothing here reads a target; every figure is
 * summed from the assigned meals.
 */
import { PLAN, META } from './data.ts';
import type { FoodItem, PlanState, SlotName } from './types.ts';
import { effectiveSessionId } from './reschedule.ts';
import type { ScheduleEntry } from './reschedule.ts';

export interface Macros { k: number; p: number; f: number; c: number }

export const ZERO: Macros = { k: 0, p: 0, f: 0, c: 0 };

/** Atwater factors. Section 3: "Every meal's calorie and macro figures are computed
 *  from its ingredient list. No asserted totals." A meal you log yourself has no
 *  ingredient list, so its calories are computed from its macros rather than typed in. */
export const KCAL_PER_G = { protein: 4, carb: 4, fat: 9 } as const;

export function kcalFromMacros(protein: number, fat: number, carb: number): number {
  return Math.round(protein * KCAL_PER_G.protein + fat * KCAL_PER_G.fat
    + carb * KCAL_PER_G.carb);
}

/** A meal you ate instead of the planned one. */
export interface CustomMeal { n: string; k: number; p: number; f: number; c: number }

export const customKey = (dayIndex: number, slot: SlotName): string =>
  `custom:${dayIndex}:${slot}`;

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** Stored values are validated rather than trusted; a malformed one falls back to the
 *  planned meal instead of poisoning every calorie figure on the screen. */
export function isCustomMeal(v: unknown): v is CustomMeal {
  if (!v || typeof v !== 'object') return false;
  const m = v as Partial<CustomMeal>;
  return typeof m.n === 'string' && m.n.trim() !== ''
    && num(m.k) && num(m.p) && num(m.f) && num(m.c);
}

export function customMealFor(
  state: PlanState, dayIndex: number, slot: SlotName,
): CustomMeal | null {
  const v = state.get<unknown>(customKey(dayIndex, slot));
  return isCustomMeal(v) ? v : null;
}

export function isCustom(state: PlanState, dayIndex: number, slot: SlotName): boolean {
  return customMealFor(state, dayIndex, slot) !== null;
}

/** meta.scheduleByDow spells the recovery days differently from the key format. */
const SCHEDULE_ALIASES: Record<string, string> = {
  liftA: 'liftA', liftB: 'liftB', run0: 'run0', run1: 'run1', run2: 'run2',
  recoveryWed: 'recWed', recoverySun: 'recSun',
};

/**
 * Which sessions earn the post-training slot, DERIVED from the plan rather than assumed.
 *
 * meta.postSlotDows names days; meta.scheduleByDow says which session sits on each. The
 * two together resolve to liftA, liftB, run0 and run2 — and not run1, which is the short
 * frequency run, nor either recovery day. The split is clean: no session appears on both
 * sides, so the post slot is a property of the SESSION, not of the calendar.
 *
 * That matters once training can move. The post slot is about 200 kcal and 21-24g of
 * protein, which is the entire difference between a training day and a lighter one. If
 * training shifts and the slot stays put, you eat the surplus on a rest day and go light
 * on a lifting day, which is exactly backwards.
 */
export const POST_SLOT_SESSIONS: ReadonlySet<string> = new Set(
  META.postSlotDows.map((dow) => META.scheduleByDow[String(dow)])
    .map((s) => SCHEDULE_ALIASES[s] ?? s),
);

export function sessionEarnsPostSlot(session: ScheduleEntry): boolean {
  return POST_SLOT_SESSIONS.has(session);
}

/** The eating slots for a day, following whichever session actually happens on it. */
export function slotsFor(state: PlanState, dayIndex: number): SlotName[] {
  const session = effectiveSessionId(state, dayIndex);
  return META.slots.filter((s) => s !== 'post' || sessionEarnsPostSlot(session));
}

/** True when the day carries the training-day surplus. */
export function isTrainingDay(state: PlanState, dayIndex: number): boolean {
  return sessionEarnsPostSlot(effectiveSessionId(state, dayIndex));
}

/**
 * mealIndex = (dayIndex + offset) % length, unless a swap is stored for that day and
 * slot, in which case the stored index wins.
 */
export function mealIndexFor(state: PlanState, dayIndex: number, slot: SlotName): number {
  const options = PLAN.food[slot];
  const swap = state.get<number>(`swap:${dayIndex}:${slot}`);
  if (typeof swap === 'number' && Number.isInteger(swap) && swap >= 0) {
    return swap % options.length;
  }
  return (dayIndex + META.mealRotationOffsets[slot]) % options.length;
}

/** The meal the PLAN assigns: rotation, or a swap to another planned option. Ignores
 *  anything you logged yourself, so it can be shown as the baseline you departed from. */
export function plannedMealFor(state: PlanState, dayIndex: number, slot: SlotName): FoodItem {
  return PLAN.food[slot][mealIndexFor(state, dayIndex, slot)];
}

/**
 * What is actually assigned to this slot. A meal you logged yourself wins over a swap,
 * which wins over the rotation.
 *
 * Section 5 says the day's calorie figure is whatever the assigned meals add up to, so
 * once you have told the app what you really ate, that IS the day's figure. Every
 * calorie and protein total in the app follows from here.
 */
export function mealFor(state: PlanState, dayIndex: number, slot: SlotName): FoodItem {
  const custom = customMealFor(state, dayIndex, slot);
  if (custom) {
    return { n: custom.n, k: custom.k, p: custom.p, f: custom.f, c: custom.c,
             t: '', d: '' };
  }
  return plannedMealFor(state, dayIndex, slot);
}

/** Swapping cycles to the next option. */
export function nextSwapIndex(state: PlanState, dayIndex: number, slot: SlotName): number {
  return (mealIndexFor(state, dayIndex, slot) + 1) % PLAN.food[slot].length;
}

function add(a: Macros, m: FoodItem): Macros {
  return { k: a.k + m.k, p: a.p + m.p, f: a.f + m.f, c: a.c + m.c };
}

/** Everything the day plans, whether ticked or not. This IS the day's calorie figure. */
export function dayPlan(state: PlanState, dayIndex: number): Macros {
  return slotsFor(state, dayIndex)
    .reduce((acc, s) => add(acc, mealFor(state, dayIndex, s)), { ...ZERO });
}

/** What the day would have been without anything you logged yourself. */
export function dayPlanBaseline(state: PlanState, dayIndex: number): Macros {
  return slotsFor(state, dayIndex)
    .reduce((acc, s) => add(acc, plannedMealFor(state, dayIndex, s)), { ...ZERO });
}

export function customCountFor(state: PlanState, dayIndex: number): number {
  return slotsFor(state, dayIndex).filter((s) => isCustom(state, dayIndex, s)).length;
}

/** Only the slots actually ticked done. What the meters count. */
export function eatenSoFar(state: PlanState, dayIndex: number): Macros {
  return slotsFor(state, dayIndex)
    .filter((s) => state.get(`meal:${dayIndex}:${s}`) === 1)
    .reduce((acc, s) => add(acc, mealFor(state, dayIndex, s)), { ...ZERO });
}

export function mealsTicked(state: PlanState, dayIndex: number): number {
  return slotsFor(state, dayIndex).filter((s) => state.get(`meal:${dayIndex}:${s}`) === 1).length;
}

/** Shopping list weights: how often each option comes up across the whole plan. */
export function rotationFrequency(state: PlanState, days = 112): Map<string, number> {
  const counts = new Map<string, number>();
  for (let d = 0; d < days; d++) {
    for (const slot of slotsFor(state, d)) {
      const m = plannedMealFor(state, d, slot);   // the rotation, not what you ate
      counts.set(`${slot}:${m.n}`, (counts.get(`${slot}:${m.n}`) ?? 0) + 1);
    }
  }
  return counts;
}
